import { EmaQuestionnaireSchema } from '../questionnaire/entity';
import { InitialQuestionnaireSchema } from '../questionnaire/entity';
import { TargetPopulationSchema } from '../target-population';
import { z } from 'zod';
import { StudyCategorySchema } from './category';

const StudyId = z.object({
  id: z.string().uuid(),
});

const StudyContent = z.object({
  name: z.string().max(75),
  contactMail: z.string().email(),
  contactName: z.string(),
  shortDescription: z.string().max(520),
  longDescription: z.string(),
  subscriptionValidityDayInterval: z.number().int().positive(),
  consentPdfUrl: z.string().url(),
  consentPdfName: z.string(),
  allowParticipantsToViewPreviousResponses: z.boolean(),
  participantDurationInMinutes: z.number().int().positive(),
});

const StudyAudit = z.object({
  createdAt: z.string().datetime(),
  publishedAt: z.string().datetime(),
  publishedBy: z.string().uuid(),
});

const StudyTimeframe = z.object({
  temporalStatus: z.enum(['upcoming', 'ongoing', 'past']),
  startDate: z.string().datetime(),
  endDate: z.string().datetime(),
});

const StudyRelations = z.object({
  category: StudyCategorySchema.optional(),
  initialQuestionnaire: InitialQuestionnaireSchema,
  emaQuestionnaires: z.array(EmaQuestionnaireSchema),
  targetPopulation: TargetPopulationSchema,
});

const DraftStudySchema = z
  .object({
    status: z.literal('draft'),
  })
  .merge(StudyId)
  .merge(StudyAudit.partial())
  .merge(StudyContent.partial())
  .merge(StudyTimeframe.partial());

const PublishedStudySchema = z
  .object({
    status: z.enum(['published', 'finished']),
  })
  .merge(StudyId)
  .merge(StudyAudit)
  .merge(StudyContent)
  .merge(StudyTimeframe);

export const DraftStudyListItemSchema = DraftStudySchema;
export const PublishedStudyListItemSchema = PublishedStudySchema;

export const DraftStudyDetailSchema = DraftStudySchema.merge(
  StudyRelations.partial(),
);
export const PublishedStudyDetailSchema =
  PublishedStudySchema.merge(StudyRelations);

export const StudyListItemSchema = z.discriminatedUnion('status', [
  DraftStudyListItemSchema,
  PublishedStudyListItemSchema,
]);

export const StudyDetailSchema = z.discriminatedUnion('status', [
  DraftStudyDetailSchema,
  PublishedStudyDetailSchema,
]);

export type StudyListItem = z.infer<typeof StudyListItemSchema>;
export type StudyDetail = z.infer<typeof StudyDetailSchema>;

export type PublishedStudyListItem = z.infer<
  typeof PublishedStudyListItemSchema
>;
export type PublishedStudyDetail = z.infer<typeof PublishedStudyDetailSchema>;
